import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Define the shape of our user data
interface User {
  id: string;
  email: string;
  name: string;
  image: string;
  rawData: FirebaseUser;
  credits: number;
  priceModifiers: {
    '1': number;
    '3': number;
    '12': number;
  };
  // Add other user properties as needed
}

const PRICE_MODIFIERS = {
  '1': 1,
  '3': 0.9,
  '12': 0.5,
};

// Define the shape of our context
interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
  login: (userData: User) => void;
  logout: () => void;
  loading: boolean;
  accessToken: string | null;
}

const EmptyUser: User = {
  id: '',
  email: '',
  name: '',
  image: '',
  rawData: null,
  credits: 0,
  priceModifiers: PRICE_MODIFIERS,
};
// Create the context with a default value
const UserContext = createContext<UserContextType | undefined>(undefined);

// Create a provider component
interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>(EmptyUser);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Set up Firebase auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          // User is signed in
          const userData: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || '',
            image: firebaseUser.photoURL || '',
            rawData: firebaseUser,
            credits: 0,
            priceModifiers: PRICE_MODIFIERS,
          };

          getUserDocument(firebaseUser.email || firebaseUser.uid, userData);
        } else {
          // User is signed out
          setUser(null);
        }
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    if (user) {
      auth.currentUser
        ?.getIdToken(true)
        .then((idToken) => {
          setAccessToken(idToken);
        })
        .catch((error) => {
          console.log('Failed to get token', error);
        });
    }
  }, [user]);

  const getUserDocument = async (identifier: string, userData: User) => {
    const userDoc = await getDoc(doc(db, 'users', identifier));
    if (userDoc.exists()) {
      const data = userDoc.data();
      Object.assign(userData.priceModifiers, data.priceModifiers ?? {}),
        setUser({
          ...userData,
          credits: data.credits,
        });
    } else {
      setUser(userData);
    }
  };

  const login = (userData: User) => {
    setUser(userData);
  };

  const logout = () => {
    setUser(null);
  };

  const value = {
    user,
    setUser,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    accessToken,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Create a custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};
